import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEditPersonalDetails } from 'lib/hooks';
import { MORE_PERSONAL_DETAILS } from 'constants/shared/routes';
import DataCheck from 'components/shared/generic/dataCheck/presentational/DataCheck';
import PageContainer from 'components/shared/generic/pageContainer/presentational/PageContainer';
import SectionTitle from 'components/shared/generic/sectionTitle/presentational/SectionTitle';
import { dispatchShowModal } from 'utils/modals';
import { INFO_MODAL } from 'constants/shared/modalTypes';
import useTranslate from 'lib/languages/useTranslate';
import { PersonalDetailSection } from 'lib/type';
import { SUITS_ME_PHONE } from 'lib/constants/routes';
import {
    getUpdateErrorContent,
    getUpdateSuccessContent,
    PersonalDetailsModalContent,
} from 'lib/utils/personalDetailsModal';
import EditAddressDetails from '../presentational/EditAddressDetails';
import EditContactDetails from '../presentational/EditContactDetails';
import { useGetAccountProfileQuery } from 'lib/services';

const EditPersonalDetailsContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { data, isLoading } = useGetAccountProfileQuery();
    const translate = useTranslate('personalDetails');
    const modalTranslate = useTranslate('personalDetails.modal');

    const { editPart } = location.state || {};

    const {
        formData,
        onChange,
        onSubmit,
        isPosting,
        error,
        handleSwitchClick,
        resetData,
    } = useEditPersonalDetails(handlePostSuccess);

    function handlePostSuccess() {
        navigate(MORE_PERSONAL_DETAILS);
    }

    const showInfoModal = ({ descriptionKey, showCallLink }: PersonalDetailsModalContent) => {
        dispatchShowModal(dispatch, INFO_MODAL, {
            title: modalTranslate('title'),
            descriptionText: modalTranslate(descriptionKey),
            confirmText: modalTranslate('confirm'),
            ...(showCallLink && {
                linkText: modalTranslate('clickCall'),
                linkHref: SUITS_ME_PHONE,
            }),
        });
    };

    const handleFormSubmit = async (section: PersonalDetailSection) => {
        try {
            await onSubmit();
            showInfoModal(getUpdateSuccessContent(section));
        } catch (submitError: unknown) {
            showInfoModal(getUpdateErrorContent(submitError));
        }
    };

    const renderEditPart = () => {
        const commonProps = {
            data,
            ...formData,
            handleChange: onChange,
            handleSubmit: handleFormSubmit,
            isPosting,
            error,
            handleSwitchClick,
            resetData,
        };

        const isAddress = editPart === PersonalDetailSection.ADDRESS;
        const Component = isAddress ? EditAddressDetails : EditContactDetails;

        return <Component {...(commonProps as any)} />;
    };

    return (
        <PageContainer className="more-edit-personal-details-container">
            <SectionTitle
                title={translate('form.editPersonalDetails')}
                backRoute={MORE_PERSONAL_DETAILS}
            />
            <DataCheck
                data={data}
                isLoading={isLoading}
                noDataMessage={translate('noDataMessage')}
            >
                {renderEditPart()}
            </DataCheck>
        </PageContainer>
    );
};

export default EditPersonalDetailsContainer;
